import { InboxRealtimeEventDto, messageReceived } from '@spike/omnichannel-action';
import { useAuth } from 'hooks';
import { useOmnichannelInboxEnabled } from 'hooks/useOmnichannelInboxEnabled';
import { InboxWebSocketsMessage } from 'model/WebSockets';
import { FunctionComponent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import { useActionCableChannel } from './useActionCableChannel';
import { useMessagesChannelMarketplace } from './MessagesChannelMarketplaceContext';
import { websocketLog } from './websocketLog';

const InboxWebSockets: FunctionComponent = () => {
    const auth = useAuth();
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const omnichannelInboxEnabled = useOmnichannelInboxEnabled();
    const { effectiveMarketplaceId } = useMessagesChannelMarketplace();

    const onReceived = useCallback(
        (event: InboxWebSocketsMessage) => {
            if (!event.message || !event.conversation || !event.unread_totals) {
                websocketLog('message', 'InboxWebSockets', { ignoredPayload: true });
                return;
            }

            websocketLog('message', 'InboxWebSockets', { conversationId: event.conversation.id });

            try {
                // Do not POST /read here: selectedConversation stays in Redux after leaving /inbox
                // and would clear the header badge. Mark-read belongs to useInbox on open thread.
                dispatch(messageReceived(event as InboxRealtimeEventDto));
            } catch {
                websocketLog('message', 'InboxWebSockets', { inboxConvertFailed: true });
            }
        },
        [dispatch]
    );

    // Unified inbox stream (backend InboxChannel, membership-checked) — NOT the legacy MessagesChannel.
    useActionCableChannel<InboxWebSocketsMessage>({
        channel: 'InboxChannel',
        params: { marketplace_id: effectiveMarketplaceId },
        enabled: Boolean(auth.signedIn && omnichannelInboxEnabled && effectiveMarketplaceId),
        onReceived,
        subscriptionDeps: [effectiveMarketplaceId, omnichannelInboxEnabled],
        logLabel: 'InboxWebSockets'
    });

    return null;
};

export default InboxWebSockets;
